/**
 * Типізований клієнт API Єдиної робочої системи Правдоп.
 * Типи згенеровано з контракту OpenAPI (docs/api/openapi.json): `npm run generate -w @ers/sdk`.
 *
 * Приклад для інтеграції (службовий токен):
 *   const ers = createErsClient({ baseUrl: 'https://ers.pravdop.com', token: process.env.ERS_TOKEN });
 *   const { data, error } = await ers.GET('/api/v1/tasks', { params: { query: { assignee: 'me' } } });
 */
import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { components, paths } from './schema.js';

export type { components, paths };
export type Schemas = components['schemas'];
export type Problem = {
  type: string;
  title: string;
  status: number;
  code: string;
  detail: string;
  errors?: { field: string; message: string }[];
  requestId?: string;
};

export type ErsClientOptions = {
  /** Адреса сервера без /api (для вебзастосунку на тому самому домені — порожній рядок). */
  baseUrl?: string;
  /** Токен ers_pat_… або ers_svc_… Для вебзастосунку не потрібен: використовується сеанс (cookie). */
  token?: string;
  /** Назва ШІ-агента для позначки походження створених об’єктів. */
  agent?: string;
  /** Режим вебзастосунку: сеанс, заголовок X-ERS-Client: web. */
  web?: boolean;
  fetch?: typeof fetch;
};

export type ErsClient = Client<paths>;

export function createErsClient(opts: ErsClientOptions = {}): ErsClient {
  const headers: Record<string, string> = {};
  if (opts.token) headers.authorization = `Bearer ${opts.token}`;
  if (opts.agent) headers['x-ers-agent'] = opts.agent;
  if (opts.web) headers['x-ers-client'] = 'web';
  return createClient<paths>({
    baseUrl: opts.baseUrl ?? '',
    headers,
    credentials: opts.web ? 'include' : undefined,
    fetch: opts.fetch,
  });
}

/** Проміжний обробник: новий ключ ідемпотентності для кожного POST без власного ключа. */
export const idempotencyMiddleware: Middleware = {
  onRequest({ request }) {
    if (request.method === 'POST' && !request.headers.has('idempotency-key')) {
      request.headers.set('idempotency-key', crypto.randomUUID());
    }
    return request;
  },
};

/** Перебирає всі сторінки курсорної пагінації. */
export async function* paginate<T>(
  fetchPage: (cursor?: string) => Promise<{ data: T[]; nextCursor?: string | null } | undefined>,
): AsyncGenerator<T> {
  let cursor: string | undefined;
  do {
    const page = await fetchPage(cursor);
    if (!page) return;
    for (const item of page.data) yield item;
    cursor = page.nextCursor ?? undefined;
  } while (cursor);
}
